import type { ISalesReportExportCmd } from "@/data/commands/order/SalesReportExportCmd";
import { Factory } from "@/data/dao/Factory";
import { DaoError, type IDataSource, type IPaginatedResult } from "@/data/dao/service/BaseServiceDao";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { OrderDao } from "@/data/dao/service/OrderDao";
import { SalesReportViewType } from "@/data/model/Enums";
import type { IFulfillmentGroup } from "@/data/model/FulfillmentGroup";
import type { IOffer } from "@/data/model/Offer";
import type { IPaginatedQry } from "@/data/queries/PaginatedQry";
import { exceptionTypes, role } from "@/data/util/Config";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";
import { Misc } from "@/data/util/Misc";

import { BaseModal } from "@/shared/elements/BaseModal";
import { translate } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";
import { style } from "@/shared/ui/Style";

import { i18n, isInRole } from "@/portal/Trans";
import { DatePicker } from "@/portal/formControls/DatePicker";
import { Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { TimePicker } from "@/portal/formControls/TimePicker";
import { TastierGlobal } from "@/portal/globalAccess";
import { UiNotification } from "@/portal/ui/Notification";
import type { IReportFilterEntityBase } from "@/data/model/ReportFilterEntityBase";
import type { IReportFilterOptionsQry } from "@/data/queries/location/ReportFilterOptionsQry";
import { trans } from "@/operator/localization/Trans";

export class SalesReportExportWindow extends BaseModal {
    private $form: JQuery;
    private $submitBtn: JQuery;

    private userSelect: Select<IReportFilterEntityBase>;
    private roomSelect: Select<IReportFilterEntityBase>;
    private offerSelect: Select<IOffer>;
    private fulfillmentGroupSelect: Select<IFulfillmentGroup>;
    private fromDatePicker: DatePicker;
    private fromTimePicker: TimePicker;
    private toDatePicker: DatePicker;
    private toTimePicker: TimePicker;
    private $isDailySummaryReport: JQuery;
    private $isPaymentMethodsReport: JQuery;
    private $isOrderFullfilmentReport: JQuery;
    private $viewTypeGroup: JQuery;
    private viewTypeSelect: Select<SalesReportViewType>;
    private $detailedNameGroup: JQuery;
    private useDetailedNamesToggle: Switch;
    private abortController?: AbortController;

    constructor() {
        super(i18n("modal.salesReportExport.title"), "modal-lg", false);

        this.$element.addClass("salesexport");

        this.$form = $('<form class="kt-form kt-form--label-right" />').appendTo(this.$body);

        const createSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");
        createSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.salesReportExport.exportSetting") +
                "</h3></div></div>"
        );

        const usersDs: IDataSource<IReportFilterEntityBase> = {
            getData: (filter: IReportFilterOptionsQry) => {
                filter.locationId = TastierGlobal.getLocationSwitch()!.getCurrentLocationId();
                return new Promise((resolve, reject) => {
                    if (filter.locationId == null) {
                        reject();
                        return;
                    }
                    return Factory.getInstance<LocationDao>(LocationDao)
                        .getLocationUserFilterOptions(filter.locationId)
                        .then((items) => {
                            
                            const k: IPaginatedResult<IReportFilterEntityBase> = {
                                limit: items.length,
                                items: items,
                                hasNext: false,
                                hasPrev: false,
                                offset: 0,
                            };

                            resolve(k);
                        })
                        .catch((ex) => {
                            logError(ex);
                            reject();
                        });
                });
            },
        };

        const roomsDataSource: IDataSource<IReportFilterEntityBase> = {
            getData: (filter: IReportFilterOptionsQry) => {
                filter.locationId = TastierGlobal.getLocationSwitch()!.getCurrentLocationId();
                return new Promise((resolve, reject) => {
                    if (filter.locationId == null) {
                        reject();
                        return;
                    }
                    return Factory.getInstance<LocationDao>(LocationDao)
                        .getLocationRoomFilterOptions(filter.locationId)
                        .then((items) => {

                            const k: IPaginatedResult<IReportFilterEntityBase> = {
                                limit: items.length,
                                items: items,
                                hasNext: false,
                                hasPrev: false,
                                offset: 0,
                            };

                            resolve(k);
                        })
                        .catch((ex) => {
                            logError(ex);
                            reject();
                        });
                });
            },
        };

        const waiterGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.entities.user") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        
        this.userSelect = new Select<IReportFilterEntityBase>(
            undefined,
            usersDs,
            (c) => { 
                if (c.id === "endUsers") {
                    return i18n("useCase.endUsers");
                } 
                
                if (c.id === "all") {
                    return i18n("general.all");
                }
                
                return c.name;
            },
            false,
            undefined,
            undefined,
            false
        ).renderTo(waiterGroup);

        const roomGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.entities.rooms") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        
        this.roomSelect = new Select<IReportFilterEntityBase>(
            undefined,
            roomsDataSource,
            (c) => {
                return c.name;
            },
            false,
            trans.general.all,
            undefined,
            true,
            undefined,
            undefined,
        ).renderTo(roomGroup);

        const offerGroup = $(
            `<div class="form-group row"><label class="col-xl-4 col-form-label">${i18n("general.entities.offers")} <span class="mandatory">*</span></label><div class="col-xl-6" /></div>`
        )
            .appendTo(createSection)
            .find("> .col-xl-6");

        this.offerSelect = new Select<IOffer>(
            undefined,
            {
                getData(filter: IPaginatedQry, abortSignal?: AbortSignal): Promise<IPaginatedResult<IOffer>> {
                    return Factory.getInstance<CatalogDao>(CatalogDao).getOffers(
                        {
                            includeChildOffers: true,
                            ...filter,
                        },
                        abortSignal
                    );
                },
            },
            (c) => c.name,
            false,
            i18n("general.all"),
            undefined,
            true
        ).renderTo(offerGroup);

        const fulfillmentGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.entities.fulfillmentGroups") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        this.fulfillmentGroupSelect = new Select<IFulfillmentGroup>(
            undefined,
            {
                getData(
                    filter: IPaginatedQry,
                    abortSignal?: AbortSignal
                ): Promise<IPaginatedResult<IFulfillmentGroup>> {
                    return Factory.getInstance<CatalogDao>(CatalogDao).getFulfillmentGroups(
                        {
                            includeFromChildLocations: true,
                            ...filter,
                        },
                        abortSignal
                    );
                },
            },
            (c) => {
                if (c.internalName !== undefined) return Format.htmlEscape(c.internalName)!;

                return Format.htmlEscape(translate(c.name))!;
            },
            false,
            i18n("general.all"),
            undefined,
            true
        ).renderTo(fulfillmentGroup);

        const endOfDay = this.getEndOfDay();
        const midDay = DateTime.todayAsUtcMidnight();

        const fromGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.timeFormat.start") +
                ' <span class="mandatory">*</span></label><div class="col-xl-3 date"></div><div class="col-xl-3 time"></div></div>'
        ).appendTo(createSection);
        this.fromDatePicker = new DatePicker(undefined, { endDate: endOfDay })
            .setValue(midDay)
            .renderTo(fromGroup.find("> .col-xl-3.date"));
        this.fromTimePicker = new TimePicker(undefined, {
            minuteStep: 1,
            secondStep: 1,
            maxHours: 24,
            defaultTime: "00:00:00",
        }).renderTo(fromGroup.find("> .col-xl-3.time"));

        const toGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("general.timeFormat.end") +
                ' <span class="mandatory">*</span></label><div class="col-xl-3 date"></div><div class="col-xl-3 time"></div></div>'
        ).appendTo(createSection);
        this.toDatePicker = new DatePicker(undefined, { endDate: endOfDay })
            .setValue(midDay)
            .renderTo(toGroup.find("> .col-xl-3.date"));
        
        this.toTimePicker = new TimePicker(undefined, {
            minuteStep: 1,
            secondStep: 1,
            maxHours: 24,
            defaultTime: "current",
        }).renderTo(toGroup.find("> .col-xl-3.time"));

        this.toTimePicker.setValue(DateTime.toTimeString(endOfDay, true));

        this.$viewTypeGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.salesReportExport.levelOfDetail") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        )
            .appendTo(createSection)
            .find("> .col-xl-6");
        const viewTypeSelect = $('<select class="form-control" />').appendTo(this.$viewTypeGroup);
        Misc.eachEnum(SalesReportViewType, (i, e) => {
            viewTypeSelect.append('<option value="' + i + '">' + i18n("enums.SalesReportViewType." + e) + "</option>");
        });
        this.viewTypeSelect = Select.createFromSelect(viewTypeSelect);
        this.viewTypeSelect.onChange = () => this.onViewTypeChange();
        this.$detailedNameGroup = $(
            '<div class="form-group row"><label class="col-xl-4 col-form-label">' +
                i18n("modal.salesReportExport.useDetailedNames") +
                ' <span class="mandatory">*</span></label><div class="col-xl-6" /></div>'
        ).appendTo(createSection);
        this.useDetailedNamesToggle = new Switch(
            $('<input data-switch="true" type="checkbox" name="isavailable" />').appendTo(
                this.$detailedNameGroup.find("> .col-xl-6")
            )
        );

        this.onViewTypeChange();

        this.$form.append(
            '<div class="kt-separator kt-separator--border-dashed kt-separator--portlet-fit kt-separator--space-lg" />'
        );

        const viewSection = $('<div class="kt-section kt-section--first"><div class="kt-section__body" /></div>')
            .appendTo(this.$form)
            .find("> .kt-section__body");

        viewSection.append(
            '<div class="row"><label class="col-xl-4"></label><div class="col-lg-9 col-xl-6"><h3 class="kt-section__title kt-section__title-sm">' +
                i18n("modal.salesReportExport.specialReports") +
                "</h3></div></div>"
        );

        const reportTypesGroup = $('<div class="form-group row"></div>').appendTo(viewSection);
        $('<label class="col-xl-4"></label>').appendTo(reportTypesGroup);

        const reportTypesContainer = $('<div style="display: flex; flex-direction: column" />').appendTo(
            reportTypesGroup
        );
        this.$isPaymentMethodsReport = $(
            `<label class="paymentMethodsReport reportTypeCheckBox"><input type="checkbox" class="form-control" /> ${i18n("modal.salesReportExport.paymentMethodsReport")}</label>`
        )
            .appendTo(reportTypesContainer)
            .find("> input");

        this.$isPaymentMethodsReport.change(() => {
            this.onPaymentMethodsReportCheck();
        });

        this.$isDailySummaryReport = $(
            '<label class="dailySummaryReport reportTypeCheckBox"><input type="checkbox" class="form-control" /> ' +
                i18n("modal.salesReportExport.dailySummaryReport") +
                "</label>"
        )
            .appendTo(reportTypesContainer)
            .find("> input");

        this.$isDailySummaryReport.change(() => {
            this.onDailySummaryReportCheck();
        });

        this.$isOrderFullfilmentReport = $(
            `<label class="orderFullfilmentReport reportTypeCheckBox"><input type="checkbox" class="form-control" /> ${i18n("modal.salesReportExport.orderFulfillmentReport")}</label>`
        )
            .appendTo(reportTypesContainer)
            .find("> input");

        this.$isOrderFullfilmentReport.change(() => {
            this.onOrderFulfillmentReportCheck();
        });

        $('<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.close();
            });

        this.$submitBtn = $('<button type="button" class="btn btn-success">' + i18n("general.export") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();
                this.$form.submit();
            });

        this.$form.submit((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.onSalesReportExport();

            return false;
        });
    }

    private getEndOfDay(): Date {
        const endOfDay = new Date();
        endOfDay.setHours(23, 59, 59, 999);
        return endOfDay;
    }

    protected onShow() {}

    protected onHide() {
        this.abortController?.abort();
    }

    private onDailySummaryReportCheck() {
        this.toDatePicker.setDisabled(false);
        if (this.$isDailySummaryReport.is(":checked")) {
            const endOfDay = new Date();
            endOfDay.setHours(23, 59, 59, 999);

            const beginOfDay = new Date();
            beginOfDay.setHours(0, 0, 0);

            this.toTimePicker.setValue(DateTime.toTimeString(endOfDay, true)).setDisabled(true);
            this.fromTimePicker.setValue(DateTime.toTimeString(beginOfDay, true)).setDisabled(true);
            this.$viewTypeGroup.find("button").prop("disabled", true);

            // Disable check boxes
            this.$isPaymentMethodsReport.prop("checked", false);
            this.$isPaymentMethodsReport.prop("disabled", true);

            this.$isOrderFullfilmentReport.prop("checked", false);
            this.$isOrderFullfilmentReport.prop("disabled", true);
        } else {
            this.toTimePicker.setDisabled(false);
            this.fromTimePicker.setDisabled(false);
            this.$viewTypeGroup.find("button").prop("disabled", false);

            // Enable check boxes
            this.$isPaymentMethodsReport.prop("disabled", false);
            this.$isOrderFullfilmentReport.prop("disabled", false);
        }
    }

    private onPaymentMethodsReportCheck() {
        if (this.$isPaymentMethodsReport.is(":checked")) {
            this.$viewTypeGroup.find("button").prop("disabled", true);

            this.$isDailySummaryReport.prop("checked", false);
            this.$isDailySummaryReport.prop("disabled", true);

            this.$isOrderFullfilmentReport.prop("checked", false);
            this.$isOrderFullfilmentReport.prop("disabled", true);
        } else {
            this.$viewTypeGroup.find("button").prop("disabled", false);

            this.$isDailySummaryReport.prop("disabled", false);
            this.$isOrderFullfilmentReport.prop("disabled", false);
        }
    }

    private onOrderFulfillmentReportCheck() {
        if (this.$isOrderFullfilmentReport.is(":checked")) {
            const endOfDay = this.getEndOfDay();
            endOfDay.setDate(endOfDay.getDate() + 93);

            this.toDatePicker.setEndDate(endOfDay);
            this.fromDatePicker.setEndDate(endOfDay);

            this.$viewTypeGroup.find("button").prop("disabled", false);

            this.$isDailySummaryReport.prop("checked", false);
            this.$isDailySummaryReport.prop("disabled", true);

            this.$isPaymentMethodsReport.prop("checked", false);
            this.$isPaymentMethodsReport.prop("disabled", true);
        } else {
            const endOfDay = this.getEndOfDay();
            this.toDatePicker.setEndDate(endOfDay);
            this.fromDatePicker.setEndDate(endOfDay);

            // Check if dates are not out of range
            const toDate = this.toDatePicker.getValue();
            const fromDate = this.fromDatePicker.getValue();

            const midDay = DateTime.todayAsUtcMidnight();

            if (toDate == null || toDate > endOfDay) {
                this.toDatePicker.setValue(midDay);
            }

            if (fromDate == null || fromDate > endOfDay) {
                this.fromDatePicker.setValue(midDay);
            }

            this.$viewTypeGroup.find("button").prop("disabled", false);

            this.$isDailySummaryReport.prop("disabled", false);
            this.$isPaymentMethodsReport.prop("disabled", false);
        }
    }

    private onViewTypeChange() {
        const viewType: SalesReportViewType = parseInt(this.viewTypeSelect.getValue()!);
        if (viewType === SalesReportViewType.Detail) this.$detailedNameGroup.show();
        else this.$detailedNameGroup.hide();
    }

    private async onSalesReportExport() {
        const isDailySummaryReport = this.$isDailySummaryReport.is(":checked");
        const isPaymentMethodsReport = this.$isPaymentMethodsReport.is(":checked");
        const isOrderFulfillmentReport = this.$isOrderFullfilmentReport.is(":checked");
        const viewType: SalesReportViewType = parseInt(this.viewTypeSelect.getValue()!);
        const selectedUserOption = this.userSelect.getModel();

        const data: ISalesReportExportCmd = {
            isDailySummary: isDailySummaryReport,
            isPaymentMethods: isPaymentMethodsReport,
            isOrderFulfillment: isOrderFulfillmentReport,
            roomIds: this.roomSelect.getValues(),
            offerIds: this.offerSelect.getValues(),
            fulfillmentGroupIds: this.fulfillmentGroupSelect.getValues(),
            from: this.fromDatePicker.getValue(),
            to: this.toDatePicker.getValue(),
            viewType: viewType,
            user: selectedUserOption?.id === "all" || selectedUserOption?.id === "endUsers" ? undefined : selectedUserOption?.id,
            filterByEndUsers: selectedUserOption != null && selectedUserOption.id === "endUsers",
            useDetailedNames: viewType === SalesReportViewType.Detail ? this.useDetailedNamesToggle.getValue() : undefined,
        };

        let hasError = false;
        this.$form.find(".is-invalid").removeClass("is-invalid");

        if (data.from === undefined) {
            this.fromDatePicker.setError(true);
            hasError = true;
        } else {
            let startTime = this.fromTimePicker.getSeconds();
            if (startTime === undefined) startTime = 0;

            data.from.setSeconds(startTime + data.from.getTimezoneOffset() * 60);
        }

        if (data.to === undefined) {
            this.toDatePicker.setError(true);
            hasError = true;
        } else {
            let endTime = this.toTimePicker.getSeconds();
            if (endTime === undefined) endTime = 0;

            data.to.setSeconds(endTime + data.to.getTimezoneOffset() * 60);

            if (data.isDailySummary) {
                this.fromTimePicker.setError(false);
                this.toTimePicker.setError(false);
                if (data.from! > data.to) {
                    this.fromDatePicker.setError(true);
                    this.toDatePicker.setError(true);
                    hasError = true;
                } else if (!this.isReportDateRangeValid(data.from!, data.to)) {
                    this.fromDatePicker.setError(true);
                    this.toDatePicker.setError(true);
                    hasError = true;
                    UiNotification.showException(i18n("modal.salesReportExport.invalidReportDateRangeException"));
                }
            } else {
                if (data.from! >= data.to) {
                    this.fromDatePicker.setError(true);
                    this.fromTimePicker.setError(true);
                    this.toDatePicker.setError(true);
                    this.toTimePicker.setError(true);
                    hasError = true;
                } else if (!this.isReportDateRangeValid(data.from!, data.to)) {
                    this.fromDatePicker.setError(true);
                    this.fromTimePicker.setError(true);
                    this.toDatePicker.setError(true);
                    this.toTimePicker.setError(true);
                    hasError = true;
                    UiNotification.showException(i18n("modal.salesReportExport.invalidReportDateRangeException"));
                }
            }
        }

        if (hasError) return;

        this.$submitBtn.addClass(style.buttonLoaderClass).prop("disabled", true).find("> .la").remove();

        try {
            const abortController = new AbortController();
            this.abortController = abortController;
            
            await Factory.getInstance<OrderDao>(OrderDao).exportSalesReport(data, abortController.signal);
            this.close();
        } catch (ex) {
            logError(ex);
            if (ex instanceof DaoError) {
                const e = ex as DaoError;
                if (e.type === exceptionTypes.invalidReportDateRangeException) {
                    this.fromDatePicker.setError(true);
                    this.toDatePicker.setError(true);
                    if (!data.isDailySummary) {
                        this.fromTimePicker.setError(true);
                        this.toTimePicker.setError(true);
                    }
                    UiNotification.showException(i18n("modal.salesReportExport.invalidReportDateRangeException"));
                    return;
                }
            }

            // if not handled by anything else
            UiNotification.showException(i18n("modal.salesReportExport.failedExport"));
        } finally {
            this.$submitBtn.removeClass(style.buttonLoaderClass).prop("disabled", false);
        }
    }

    private isReportDateRangeValid(dateFrom: Date, dateTo: Date) {
        //make sure everbody besides admin only export 93 days
        const maxAllowedDays = 93;

        if (isInRole(role.Admin)) {
            return true;
        }

        const dayDiff = Math.round((dateTo.getTime() - dateFrom!.getTime()) / (1000 * 60 * 60 * 24));
        if (dayDiff > maxAllowedDays) {
            return false;
        }

        return true;
    }
}
